/**
 * CAPTCHA-01 (Cloudflare Turnstile, modo managed, `appearance: 'interaction-only'`).
 *
 * **Sin `VITE_TURNSTILE_SITE_KEY` no se carga ningún script ni se monta nada** (lección del
 * incidente CUENTAS-01b: faltar una variable de build no puede tumbar la web).
 *
 * **El contenedor tiene que ser un elemento real de la página, visible, no uno fuera de pantalla**
 * (incidente de producción del 2026-09-28: con el contenedor en `left:-9999px` y sin tamaño, cuando
 * Cloudflare decidía que hacía falta interacción, nadie podía verla ni pulsarla, y el widget se
 * quedaba pendiente para siempre, sin token y sin avisar. Los cuatro rechazos `CAPTCHA_INVALIDO`
 * medidos en Render esa mañana nunca llegaron a pedirle nada a `siteverify`: el frontend nunca
 * mandó ningún token). Con `interaction-only`, Cloudflare deja el widget sin alto casi siempre —
 * no hace falta ocultarlo con CSS — y solo le da altura si de verdad hay que resolver un reto.
 */
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
export const CAPTCHA_DISPONIBLE = Boolean(SITE_KEY);

const SRC_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface Turnstile {
  render(
    contenedor: HTMLElement,
    opciones: {
      sitekey: string;
      appearance: 'interaction-only';
      callback: (token: string) => void;
      'error-callback': () => void;
      'expired-callback': () => void;
    },
  ): string;
  remove(widgetId: string): void;
}
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let scriptCargado: Promise<void> | null = null;

function cargarScript(): Promise<void> {
  if (scriptCargado !== null) return scriptCargado;
  scriptCargado = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SRC_SCRIPT;
    script.async = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject(new Error('no se pudo cargar Turnstile'));
    };
    document.head.appendChild(script);
  });
  return scriptCargado;
}

/**
 * Monta el widget de Turnstile en `contenedor` (ya presente en la página, visible: debajo de la
 * caja de pregunta o del formulario de inicio de sesión) y llama a `onCambio` cada vez que cambia
 * el token: con el token en cuanto Cloudflare lo da, y con `null` si expira o si da un error, para
 * que quien llama sepa que tiene que esperar o dejar reintentar.
 *
 * Sin `VITE_TURNSTILE_SITE_KEY` no hace nada y no llama nunca a `onCambio`. Devuelve una función
 * para desmontar el widget (llamarla al desmontar el componente que lo usa).
 */
export function montarCaptcha(contenedor: HTMLElement, onCambio: (token: string | null) => void): () => void {
  if (!CAPTCHA_DISPONIBLE) return () => undefined;

  let vigente = true;
  let widgetId: string | null = null;

  void cargarScript()
    .then(() => {
      if (!vigente) return;
      const turnstile = window.turnstile;
      if (turnstile === undefined) return;
      widgetId = turnstile.render(contenedor, {
        sitekey: SITE_KEY,
        appearance: 'interaction-only',
        callback: token => {
          onCambio(token);
        },
        'error-callback': () => {
          onCambio(null);
        },
        'expired-callback': () => {
          onCambio(null);
        },
      });
    })
    .catch(() => {
      // El script no cargó (red, bloqueador de anuncios): no hay token, y no hay más que hacer;
      // quien llama ya trata `null` como "todavía no hay captcha listo".
    });

  return () => {
    vigente = false;
    if (widgetId !== null) window.turnstile?.remove(widgetId);
  };
}
